<script setup lang="ts">
import { computed } from 'vue';
import type { HelpArticle } from '@/data/help';
import HelpExpandable from './HelpExpandable.vue';
import HelpScreenshot from './HelpScreenshot.vue';
import HelpVideo from './HelpVideo.vue';
import { helpRegistry } from '@/data/help';

const props = defineProps<{
  article: HelpArticle;
}>();

const emit = defineEmits<{
  (e: 'navigate', articleId: string): void;
}>();

// Convert markdown-like content to HTML
function parseContent(content: string): string {
  if (!content) return '';

  let html = content.trim();

  // Escape HTML except for allowed tags
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Code blocks (``` ... ```)
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_match, _lang, code) => {
    return `<pre class="code-block"><code>${code.trim()}</code></pre>`;
  });

  // Inline code (`code`)
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Tables
  html = html.replace(/\|(.+)\|\n\|[-| ]+\|\n((?:\|.+\|\n?)+)/g, (_, header, body) => {
    const headerCells = header.split('|').map((c: string) => c.trim()).filter(Boolean);
    const headerRow = headerCells.map((c: string) => `<th>${c}</th>`).join('');

    const bodyRows = body.trim().split('\n').map((row: string) => {
      const cells = row.split('|').map((c: string) => c.trim()).filter(Boolean);
      return `<tr>${cells.map((c: string) => `<td>${c}</td>`).join('')}</tr>`;
    }).join('');

    return `<table class="help-table"><thead><tr>${headerRow}</tr></thead><tbody>${bodyRows}</tbody></table>`;
  });

  // Headers
  html = html.replace(/^### (.+)$/gm, '<h5>$1</h5>');
  html = html.replace(/^## (.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^# (.+)$/gm, '<h3>$1</h3>');

  // Bold and italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Lists (unordered)
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>');

  // Lists (ordered)
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Horizontal rules
  html = html.replace(/^---$/gm, '<hr>');

  // Line breaks and paragraphs
  html = html
    .split('\n\n')
    .map(para => {
      para = para.trim();
      if (!para) return '';
      // Don't wrap if already a block element
      if (para.startsWith('<h') || para.startsWith('<ul') || para.startsWith('<ol') ||
          para.startsWith('<pre') || para.startsWith('<table') || para.startsWith('<hr')) {
        return para;
      }
      return `<p>${para.replace(/\n/g, '<br>')}</p>`;
    })
    .join('\n');

  return html;
}

const parsedContent = computed(() => parseContent(props.article.content));

// Get related articles
const relatedArticles = computed(() => {
  if (!props.article.relatedArticles) return [];
  return props.article.relatedArticles
    .map(id => helpRegistry[id])
    .filter(Boolean);
});

function handleRelatedClick(articleId: string) {
  emit('navigate', articleId);
}
</script>

<template>
  <article class="help-article">
    <!-- Article Header -->
    <header class="article-header">
      <div class="article-category">{{ article.category }}</div>
      <h2 class="article-title">{{ article.title }}</h2>
      <p class="article-summary">{{ article.summary }}</p>
    </header>

    <!-- Main Content -->
    <div class="article-content" v-html="parsedContent"></div>

    <!-- Screenshots -->
    <div v-if="article.screenshots?.length" class="article-screenshots">
      <HelpScreenshot
        v-for="(screenshot, index) in article.screenshots"
        :key="index"
        :screenshot="screenshot"
      />
    </div>

    <!-- Videos -->
    <div v-if="article.videos?.length" class="article-videos">
      <HelpVideo
        v-for="(video, index) in article.videos"
        :key="index"
        :video="video"
      />
    </div>

    <!-- Expandable Sections -->
    <div v-if="article.expandableSections?.length" class="article-expandable">
      <HelpExpandable
        v-for="(section, index) in article.expandableSections"
        :key="index"
        :section="section"
      />
    </div>

    <!-- Related Articles -->
    <div v-if="relatedArticles.length" class="article-related">
      <h4 class="related-title">Related Topics</h4>
      <div class="related-links">
        <button
          v-for="related in relatedArticles"
          :key="related.id"
          class="related-link"
          @click="handleRelatedClick(related.id)"
        >
          <i class="bi bi-link-45deg"></i>
          {{ related.title }}
        </button>
      </div>
    </div>
  </article>
</template>

<style scoped>
.help-article {
  max-width: 100%;
}

.article-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.article-category {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  margin-bottom: 8px;
}

.article-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
  line-height: 1.2;
}

.article-summary {
  font-size: 15px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.article-content {
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
}

.article-content :deep(h3) {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 24px 0 12px 0;
}

.article-content :deep(h4) {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 20px 0 10px 0;
}

.article-content :deep(h5) {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 16px 0 8px 0;
}

.article-content :deep(p) {
  margin: 0 0 12px 0;
}

.article-content :deep(ul),
.article-content :deep(ol) {
  margin: 0 0 12px 0;
  padding-left: 24px;
}

.article-content :deep(li) {
  margin: 4px 0;
}

.article-content :deep(code) {
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 13px;
  color: #dc2626;
}

.article-content :deep(pre.code-block) {
  background: #1f2937;
  color: #e5e7eb;
  padding: 16px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 12px 0;
}

.article-content :deep(pre.code-block code) {
  background: none;
  padding: 0;
  color: inherit;
  font-size: 13px;
}

.article-content :deep(.help-table) {
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;
  font-size: 13px;
}

.article-content :deep(.help-table th),
.article-content :deep(.help-table td) {
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  text-align: left;
}

.article-content :deep(.help-table th) {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
}

.article-content :deep(.help-table tr:hover td) {
  background: #f9fafb;
}

.article-content :deep(strong) {
  font-weight: 600;
  color: #1f2937;
}

.article-content :deep(hr) {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 20px 0;
}

.article-screenshots,
.article-videos {
  margin: 24px 0;
}

.article-expandable {
  margin: 24px 0;
}

.article-related {
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.related-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
}

.related-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.related-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s;
}

.related-link:hover {
  background: #e5e7eb;
  border-color: #d1d5db;
  color: #1f2937;
}

.related-link i {
  color: #6b7280;
}
</style>

<!-- Dark mode styles (unscoped) -->
<style>
[data-bs-theme="dark"] .article-header {
  border-bottom-color: #334155;
}

[data-bs-theme="dark"] .article-category {
  color: #94a3b8;
}

[data-bs-theme="dark"] .article-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .article-summary {
  color: #94a3b8;
}

[data-bs-theme="dark"] .article-content {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .article-content h3,
[data-bs-theme="dark"] .article-content h4,
[data-bs-theme="dark"] .article-content h5 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .article-content code {
  background: #334155;
  color: #fca5a5;
}

[data-bs-theme="dark"] .article-content pre.code-block {
  background: #0f172a;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .article-content .help-table th,
[data-bs-theme="dark"] .article-content .help-table td {
  border-color: #334155;
}

[data-bs-theme="dark"] .article-content .help-table th {
  background: #1e293b;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .article-content .help-table tr:hover td {
  background: #1e293b;
}

[data-bs-theme="dark"] .article-content strong {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .article-content hr {
  border-top-color: #334155;
}

[data-bs-theme="dark"] .article-related {
  border-top-color: #334155;
}

[data-bs-theme="dark"] .related-title {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .related-link {
  background: #334155;
  border-color: #475569;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .related-link:hover {
  background: #475569;
  border-color: #64748b;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .related-link i {
  color: #94a3b8;
}
</style>
